import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { ButtonIcon, MaskedLink } from '@hz-design-system/web-ui';
import { Trans } from '@ecg-marktplaats/js-react-i18n';

import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import { trackGAEvent } from '../../../../utils/gaTracking';
import { appendHashParam, deleteHashParam, getHashParamValue } from '../../../../utils/hashParams';
import registerPhoneClick from '../../repositories/registerPhoneClick';
import Asq from './Asq';
import SellerContactOptionsButton from './SellerContactOptionsButton';
import OneStepBidding from '../../../BiddingApp/components/Bidding/OneStepBidding';
import createCustomDimension from '../../../../utils/createCustomDimension';
import BuyItNowButton from './BuyItNow/BuyItNowButton';
import CallAvailabilityIndicator from './CallAvailabilityIndicator';

import { GA_PARAMETER, GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';
import { CONTACT_OPTION_TYPES } from '../../../../../../common/constants';

import Classes from './SellerContactOptions.module.scss';

import { TListing, TListingClient, TListingSellerInfo } from 'types/TListing';
import { TSellerProfileClient } from 'types/TSellerProfile';

let PhoneDialog;
let LoginDialogContainer;

type TSellerContactOptionsProps = {
  itemId: TListingClient['itemId'];
  title: TListingClient['title'];
  adImageThumbnail: string;
  priceInfo: TListingClient['priceInfo'];
  seller: TListingSellerInfo;
  isAutomotiveAd: TListingClient['isAutomotiveAd'];
  allowTradeInRequest: TListingClient['flags']['allowTradeInRequest'];
  isAdmarkt: TListingClient['flags']['isAdmarkt'];
  hasCallTracking: TListing['flags']['hasCallTracking'];
  traits: TListingClient['traits'];
  profile: TSellerProfileClient;
  isCarAd?: boolean;
  isBiddingEnabled?: boolean;
  adBids: TListingClient['bidsInfo']['bids'];
  onUpdateBidsHandler: (bids: TListingClient['bidsInfo']['bids']) => void;
  currentMinimumBid?: TListingClient['bidsInfo']['currentMinimumBid'];
  isReserved: TListingClient['isReserved'];
};
type GaEventProps = {
  label: string;
};

const HASH_PARAMS = {
  SHOW_PHONE: {
    KEY: 'action',
    VALUE: 'showPhone',
  },
};

type SellersContactWebsiteButtonProps = {
  sellerWebsiteUrl?: string;
  handleWebsiteClick: () => void;
  isPrimary?: boolean;
};

const SellersContactWebsiteButton = ({
  sellerWebsiteUrl,
  handleWebsiteClick,
  isPrimary = false,
}: SellersContactWebsiteButtonProps) => {
  return (
    <MaskedLink
      target="_blank"
      rel="nofollow"
      href={sellerWebsiteUrl as string}
      className={`hz-Button ${isPrimary ? 'hz-Button--primary' : 'hz-Button--secondary'} ${Classes.button} ${Classes.link} ${isPrimary ? Classes.linkPrimary : Classes.linkSecondary}`}
      onClick={handleWebsiteClick}
    >
      <ButtonIcon name="website" isStartIcon={false} />
      <Trans tagName="span" i18nKey="sellerContact.website" />
    </MaskedLink>
  );
};

const SellerContactOptions = ({
  itemId,
  title,
  adImageThumbnail,
  priceInfo,
  seller,
  isAutomotiveAd,
  isAdmarkt,
  hasCallTracking,
  allowTradeInRequest,
  traits,
  profile,
  isCarAd,
  adBids,
  onUpdateBidsHandler,
  currentMinimumBid,
  isBiddingEnabled,
  isReserved,
}: TSellerContactOptionsProps) => {
  const {
    name: sellerName,
    phoneNumber,
    isAsqEnabled,
    sellerWebsiteUrl,
    phoneNumberHidden,
    showProfilePicture,
    showSalesRepresentatives,
    financeAvailable,
    id: sellerId,
    sellerType,
    contactOptions,
  } = seller;

  const {
    xsrfToken,
    userLoggedIn,
    userDetails: { isAdOwner = false } = {},
    hasLicensePlate,
    links: { buyItNowPathPrefix },
    isMobileDevice,
  } = useContext(EnvironmentContext);
  const withPhoneContactOption = contactOptions.includes(CONTACT_OPTION_TYPES.PHONE);

  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [isPhoneDialogLoaded, setPhoneDialogLoaded] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginSuccessAction, setLoginSuccessAction] = useState('');
  const [isPrimaryButtonSticky, setIsPrimaryButtonSticky] = useState(false);
  const primaryButtonRef = useRef<HTMLDivElement | null>(null);
  // A dedicated sentinel element that is always stable in size and is never
  // affected by the sticky-state class changes. Observing it instead of the
  // root container prevents the layout-reflow feedback loop that causes the
  // button to flicker on scroll in mobile Safari and Chrome.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Timestamp of the last sticky state change. Used to debounce rapid toggling
  // caused by layout reflows when the buttonPlaceholder class is applied.
  const lastStickyChangeRef = useRef<number>(0);

  const reloadAndShowPhoneDialog = () => {
    appendHashParam(HASH_PARAMS.SHOW_PHONE.KEY, HASH_PARAMS.SHOW_PHONE.VALUE);
    document.location.reload();
  };

  const loadPhoneDialog = async (gaEvent?: GaEventProps) => {
    if (!isPhoneDialogLoaded) {
      try {
        PhoneDialog = (await import(/* webpackChunkName: "PhoneDialog" */ './PhoneDialog')).default;
        setPhoneDialogLoaded(true);
        if (gaEvent?.label) {
          trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SPhoneBegin, `click-${gaEvent.label}`, {
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_SELLER_TYPE,
              payload: sellerType,
            }),
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_HAS_LICENSE_PLATE,
              payload: hasLicensePlate,
            }),
          });
        } else {
          trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SPhoneBegin, 'click', {
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_SELLER_TYPE,
              payload: sellerType,
            }),
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_HAS_LICENSE_PLATE,
              payload: hasLicensePlate,
            }),
          });
        }
        registerPhoneClick({ itemId, xsrfToken });
      } catch {}
    }
  };

  useEffect(() => {
    if (getHashParamValue(HASH_PARAMS.SHOW_PHONE.KEY) === HASH_PARAMS.SHOW_PHONE.VALUE) {
      deleteHashParam(HASH_PARAMS.SHOW_PHONE.KEY);
      if (!phoneNumberHidden) {
        loadPhoneDialog();
        setShowPhoneNumber(true);
      }
    }
    // keep deps empty to run useEffect hook only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobileDevice) {
      // Observe the dedicated sentinel element whose dimensions never change
      // when isPrimaryButtonSticky is toggled. Using threshold:0 means the
      // sticky bar appears only once the sentinel is fully out of view, which
      // avoids the repeated intersection-callback / layout-reflow cycle that
      // caused the button to flicker on scroll in mobile Safari and Chrome.
      const sentinelEl = sentinelRef.current as Element;
      // Debounce time in ms to prevent rapid toggling caused by layout reflows
      // when the buttonPlaceholder class adds/removes padding.
      const DEBOUNCE_MS = 100;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const now = Date.now();
            const timeSinceLastChange = now - lastStickyChangeRef.current;

            // Skip state changes that happen too quickly after the previous one.
            // This prevents the feedback loop where toggling sticky state causes
            // a layout reflow that immediately triggers another intersection event.
            if (timeSinceLastChange < DEBOUNCE_MS) {
              return;
            }

            const shouldBeSticky = !entry.isIntersecting;
            setIsPrimaryButtonSticky((prev) => {
              if (prev !== shouldBeSticky) {
                lastStickyChangeRef.current = now;
              }
              return shouldBeSticky;
            });
          });
        },
        {
          rootMargin: '-136px 0px 0px 0px',
          threshold: 0,
        },
      );

      if (sentinelRef.current) {
        observer.observe(sentinelEl);
      }

      return () => {
        if (sentinelEl) {
          observer.disconnect();
        }
      };
    }
  }, [isMobileDevice]);

  const handleWebsiteClick = () => {
    trackGAEvent(
      GA_TRACK_EVENTS_ACTIONS.R2SExternalBegin,
      'button',
      createCustomDimension({
        dimension: GA_PARAMETER.AD_SELLER_TYPE,
        payload: sellerType,
      }),
    );
  };

  const buyItNowHandler = (gaEvent?: GaEventProps) => {
    if (gaEvent) {
      trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SBINBegin, gaEvent.label);
    } else {
      trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SBINBegin);
    }
    location.assign(`${buyItNowPathPrefix}${itemId}`);
  };

  const handleBuyItNowClick = async (gaEvent?: GaEventProps) => {
    if (userLoggedIn) {
      buyItNowHandler(gaEvent);
    } else {
      LoginDialogContainer = (
        await import(
          /* webpackChunkName: "loginDialogContainer" */ '../../../../components/LoginDialog/LoginDialogContainer'
        )
      ).default;
      setShowLoginDialog(true);
      setLoginSuccessAction('buyItNow');
    }
  };

  const handlePhoneNumberClick = async (gaEvent?: GaEventProps) => {
    if (!phoneNumberHidden) {
      loadPhoneDialog(gaEvent);
      setShowPhoneNumber(true);
    } else {
      LoginDialogContainer = (
        await import(
          /* webpackChunkName: "loginDialogContainer" */ '../../../../components/LoginDialog/LoginDialogContainer'
        )
      ).default;
      setShowLoginDialog(true);
      setLoginSuccessAction('phone');
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginDialog(false);
    switch (loginSuccessAction) {
      case 'phone':
        reloadAndShowPhoneDialog();
        break;

      case 'buyItNow':
        buyItNowHandler();
        break;
    }
  };

  const handlePhoneDialogClose = () => {
    setShowPhoneNumber(false);
  };

  // do not render an empty block if nothing to show
  if ((!isAsqEnabled || (isAsqEnabled && isAdOwner)) && !sellerWebsiteUrl && !phoneNumber && !showLoginDialog) {
    return null;
  }

  return (
    <div
      className={`hz-Page-section hz-Page-section--separated ${Classes.root} ${isPrimaryButtonSticky ? Classes.buttonPlaceholder : ''}`}
      ref={primaryButtonRef}
    >
      {/* Zero-size sentinel whose layout is never mutated by sticky-state changes.
          The IntersectionObserver watches this element so that toggling
          isPrimaryButtonSticky (which adds padding / position:fixed elsewhere)
          cannot re-trigger the callback and create a flicker feedback loop. */}
      <div ref={sentinelRef} className={Classes.sentinel} aria-hidden="true" />
      {contactOptions.map((type, index) => {
        const isPrimary = index === 0;
        return (
          <Fragment key={type}>
            {withPhoneContactOption &&
              isPrimary &&
              type !== CONTACT_OPTION_TYPES.PHONE &&
              Boolean(profile.availableForCallingUntil && seller.withCallAvailabilityIndicator) && (
                <CallAvailabilityIndicator availableForCallingUntil={profile.availableForCallingUntil} />
              )}
            <div key={type} {...(isPrimary && isPrimaryButtonSticky ? { className: Classes.sticky } : {})}>
              {type === CONTACT_OPTION_TYPES.BUY_IT_NOW && (
                <BuyItNowButton
                  onClick={() => {
                    handleBuyItNowClick();
                  }}
                />
              )}
              {type === CONTACT_OPTION_TYPES.ASQ && (
                <Asq
                  itemId={itemId}
                  allowTradeInRequest={allowTradeInRequest}
                  isAutomotiveAd={isAutomotiveAd}
                  title={title}
                  sellerName={sellerName}
                  sellerLocation={seller.location}
                  isPrimary={isPrimary}
                  sellerId={sellerId}
                  adImageThumbnail={adImageThumbnail}
                  showProfilePicture={showProfilePicture}
                  showSalesRepresentatives={showSalesRepresentatives}
                  financeAvailable={financeAvailable}
                  sellerType={sellerType}
                  priceInfo={priceInfo}
                  traits={traits}
                  profile={profile}
                  isAdmarkt={isAdmarkt}
                  isCarAd={isCarAd}
                  currentMinimumBid={currentMinimumBid}
                  isBiddingEnabled={isBiddingEnabled}
                  isReserved={isReserved}
                />
              )}
              {type === CONTACT_OPTION_TYPES.PHONE && (
                <>
                  {isPrimary && Boolean(profile.availableForCallingUntil && seller.withCallAvailabilityIndicator) && (
                    <CallAvailabilityIndicator availableForCallingUntil={profile.availableForCallingUntil} />
                  )}
                  <SellerContactOptionsButton
                    isPrimary={isPrimary}
                    className={Classes.button}
                    onClick={handlePhoneNumberClick}
                  >
                    <ButtonIcon name="call" isStartIcon={false} />
                    <Trans tagName="span" i18nKey="sellerContact.showPhoneNumber" />
                  </SellerContactOptionsButton>
                </>
              )}
              {type === CONTACT_OPTION_TYPES.WEBSITE && (
                <SellersContactWebsiteButton
                  sellerWebsiteUrl={sellerWebsiteUrl}
                  handleWebsiteClick={() => handleWebsiteClick()}
                  isPrimary={isPrimary}
                />
              )}
            </div>
          </Fragment>
        );
      })}
      {isBiddingEnabled && (
        <OneStepBidding
          priceType={priceInfo.priceType}
          itemId={itemId}
          adBids={adBids}
          onUpdateBidsHandler={onUpdateBidsHandler}
          title={title}
          currentMinimumBid={currentMinimumBid}
          sellerType={sellerType}
          withPhoneNumber={isCarAd}
          isReserved={isReserved}
        />
      )}

      {isPhoneDialogLoaded && showPhoneNumber && (
        <PhoneDialog
          seller={seller}
          itemId={itemId}
          hasCallTracking={hasCallTracking}
          onClose={handlePhoneDialogClose}
        />
      )}
      {showLoginDialog && <LoginDialogContainer onClose={setShowLoginDialog} onSuccess={handleLoginSuccess} />}
    </div>
  );
};

export default SellerContactOptions;
